/**
 * Cookie Consent Example Component
 *
 * Demonstration component for ComponentsShowcase
 * Shows how to use the CookieConsent component with different configurations
 *
 * @module components/Organisms/CookieConsent/CookieConsentExample
 */

import React, { useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import CookieConsent from './CookieConsent';
import CookieSettingsButton from './CookieSettingsButton';
import { useCookieConsent } from './useCookieConsent';
import { Button } from '../../Atoms/ESSButton/Button';
import './CookieConsentExample.scss';

const messages = defineMessages({
  title: {
    id: 'Cookie Consent Component',
    defaultMessage: 'Cookie Consent Component',
  },
  description: {
    id: 'A GDPR-compliant cookie consent banner with Google Analytics integration.',
    defaultMessage:
      'A GDPR-compliant cookie consent banner with Google Analytics integration.',
  },
  currentStatus: {
    id: 'Current Consent Status',
    defaultMessage: 'Current Consent Status',
  },
  hasConsent: {
    id: 'Has made choice',
    defaultMessage: 'Has made choice',
  },
  analyticsStatus: {
    id: 'Analytics tracking',
    defaultMessage: 'Analytics tracking',
  },
  enabled: {
    id: 'Enabled',
    defaultMessage: 'Enabled',
  },
  disabled: {
    id: 'Disabled',
    defaultMessage: 'Disabled',
  },
  notSet: {
    id: 'Not set',
    defaultMessage: 'Not set',
  },
  showBanner: {
    id: 'Show Cookie Banner',
    defaultMessage: 'Show Cookie Banner',
  },
  resetConsent: {
    id: 'Reset Consent',
    defaultMessage: 'Reset Consent',
  },
});

/**
 * CookieConsentExample Component
 * For demonstration in ComponentsShowcase
 */
const CookieConsentExample = () => {
  const intl = useIntl();
  const { hasConsent, analyticsEnabled, resetConsent } = useCookieConsent();
  const [showDemo, setShowDemo] = useState(false);

  return (
    <div className="ess-cookie-consent-example">
      <div className="ess-cookie-consent-example__header">
        <h3>{intl.formatMessage(messages.title)}</h3>
        <p>{intl.formatMessage(messages.description)}</p>
      </div>

      <div className="ess-cookie-consent-example__status">
        <h4>{intl.formatMessage(messages.currentStatus)}</h4>
        <div className="ess-cookie-consent-example__status-grid">
          <div className="ess-cookie-consent-example__status-item">
            <span className="ess-cookie-consent-example__status-label">
              {intl.formatMessage(messages.hasConsent)}:
            </span>
            <span
              className={`ess-cookie-consent-example__status-value ${hasConsent ? 'positive' : 'negative'}`}
            >
              {hasConsent
                ? intl.formatMessage(messages.enabled)
                : intl.formatMessage(messages.notSet)}
            </span>
          </div>
          <div className="ess-cookie-consent-example__status-item">
            <span className="ess-cookie-consent-example__status-label">
              {intl.formatMessage(messages.analyticsStatus)}:
            </span>
            <span
              className={`ess-cookie-consent-example__status-value ${analyticsEnabled ? 'positive' : 'negative'}`}
            >
              {hasConsent
                ? analyticsEnabled
                  ? intl.formatMessage(messages.enabled)
                  : intl.formatMessage(messages.disabled)
                : intl.formatMessage(messages.notSet)}
            </span>
          </div>
        </div>
      </div>

      <div className="ess-cookie-consent-example__actions">
        <Button variant="positive" onClick={() => setShowDemo(true)}>
          {intl.formatMessage(messages.showBanner)}
        </Button>
        <CookieSettingsButton variant="negative" />
      </div>

      <div className="ess-cookie-consent-example__info">
        <h4>Features</h4>
        <ul>
          <li>✅ GDPR-compliant two-step consent flow</li>
          <li>✅ Google Analytics 4 integration</li>
          <li>✅ Persistent cookie storage (6 months)</li>
          <li>✅ Full internationalization support</li>
          <li>✅ Accessible modal interface</li>
          <li>✅ Responsive design</li>
        </ul>

        <h4>Usage</h4>
        <pre className="ess-cookie-consent-example__code">
          {`import CookieConsent from './components/Organisms/CookieConsent';

// Add to your app
<CookieConsent
  googleAnalyticsId="G-XXXXXXXXXX"
  privacyPolicyUrl="/privacy-policy"
  cookieExpiryMonths={6}
/>`}
        </pre>

        <h4>Hook Usage</h4>
        <pre className="ess-cookie-consent-example__code">
          {`import { useCookieConsent } from './components/Organisms/CookieConsent/useCookieConsent';

const { 
  hasConsent, 
  analyticsEnabled, 
  resetConsent 
} = useCookieConsent();`}
        </pre>
      </div>

      {/* Demo banner - force show */}
      {showDemo && (
        <CookieConsent
          showOnLoad={true}
          googleAnalyticsId="G-DEMO123456"
          privacyPolicyUrl="/privacy-policy"
        />
      )}
    </div>
  );
};

export default CookieConsentExample;
